// From http://www.redblobgames.com/
// Copyright 2015 Red Blob Games <redblobgames@gmail.com>
// License: Apache v2.0 <http://www.apache.org/licenses/LICENSE-2.0.html>

/* Image handling common to 2D and 3D rendering
 *
 * I'm saving all the images to static files, and using those files on
 * startup. When the diagram needs to be redrawn, I replace the <img>
 * with a <canvas> and draw it.
 *
 * This makes the page load much faster, and it also will work if
 * Javascript is disabled.
 *
 * In "generate" mode, I draw the diagrams and save them to files.
 * I'll use this during development if the drawing style has changed.
 * It would be nice to automate this; maybe later.
 */

// Use special parameter #cache to regenerate images to send to server
const SAVE_IMAGES = document.location.hash.match(/#repopulate-cache/);
if (SAVE_IMAGES) { setTimeout(() => window.close(), 30000); }

/** Draw logic:
 *
 * If there's already a canvas with the given id, use it for drawing.
 * Skip the caching behavior. Otherwise:
 *
 * If SAVE_IMAGES is set, skip the image cache. Save the image to
 * a file by sending it to a localhost server.
 *
 * Otherwise, assume there's an <img> with the same name that acts as
 * the cached copy. The first draw will keep the cached version. The
 * second draw will replace the <img> with a <canvas>. Subsequent
 * calls will draw to the canvas.
 *
 * Since many of the diagrams on the page are static, and even the
 * dynamic ones don't get any interaction, we can use the cached copy
 * most of the time.
 */
export function draw() {
    // If there's already a canvas, short circuit all the caching logic.
    let canvas = document.getElementById(this.elementId);
    if (canvas?.tagName === "CANVAS") {
        this.canvas = canvas;
        this.draw = this._draw;
        this.setup();
        this.draw();
        return;
    }

    // But if there isn't a canvas with this id, assume there's an
    // image that loads from render-${id}. Skip the *first* draw call
    // because we're using the cached image. But after this, we'll draw.
    this.draw = switch_to_canvas_and_draw;
    if (SAVE_IMAGES) {
        this.draw();
        console.log("Saving", this.elementId);
        fetch("http://localhost:8000/" + this.elementId, {
            method: 'POST',
            mode: 'no-cors',
            credentials: 'same-origin',
            headers: {'Content-Type': 'text/plain'},
            body: this.canvas.toDataURL('image/png')
        });
    }
}

function switch_to_canvas_and_draw() {
    // This is the second draw. The <img> only captures the
    // diagram on the first draw, so we can't use it anymore.
    // Replace it with a <canvas> and then regenerate the diagram.
    const img = findImageOnPage(this.elementId);
    this.canvas = createCanvas(this.elementId, img.getAttribute('width'), img.getAttribute('height'));
    
    // Only replace the image if we're able to get a drawing
    // context. This is especially important for WebGL, which
    // is not as widely supported as Canvas.
    if (!this.getContext()) {
        console.log("error: could not create drawing context for #", this.elementId);
        this.draw = function() {};
        return;
    }

    // Replace the image with the canvas
    img.parentNode.insertBefore(this.canvas, img);
    img.parentNode.removeChild(img);

    // Prepare for drawing
    this.setup();

    // Replace ourselves
    this.draw = this._draw;

    this.draw();
};


/** Create a <canvas> element for drawing

    1. Find an <img src="images/$id.png"> and replace it by <canvas>.
    2. Set this.canvas to the new canvas element.
    3. The containing class should have this.getContext() to return
       a drawing context. If it can't make one, don't replace the img.
 */
    
/** Find and return <img src="images/$id.png?…"> */
function findImageOnPage(elementId) {
    const images = document.getElementsByTagName('img');
    for (let img of images) {
        let url = img.src.replace(/\?.*$/, '').split('/');
        if (url[url.length-1] == elementId + '.png') {
            return img;
        }
    }
    return null;
}

/** Create canvas */
function createCanvas(elementId, width, height) {
    const canvas = document.createElement('canvas');
    canvas.setAttribute('id', elementId);
    canvas.setAttribute('width', width);
    canvas.setAttribute('height', height);
    return canvas;
}
